// LayerSelector.tsx

import React, {useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimes} from '@fortawesome/pro-regular-svg-icons';
import {Skeleton} from '@material-ui/lab'; // <-- Import Skeleton
import './LayerSelector.scss';

interface LayerOption {
  key: string;
  icon: React.ReactNode;
  label: string;
}

interface LayerSelectorProps {
  selectedLayer: LayerOption;
  onSelect: (layerKey: string) => void;
  onClose: () => void;
  layerOptions: LayerOption[];
  onRemoveLayer?: () => void;
  selectedDateLabel?: string;
  canViewPastLayers?: boolean;
  onViewPastLayers?: () => void;
  droneLayerOptions: LayerOption[];
  selectedDroneLayer: LayerOption | null;
  // NEW: loading indicator (e.g. NDVI / layer data still fetching)
  layersLoading: boolean;
  onDroneLayerSelect: (layerKey: string) => void;
  onRemoveDroneLayer?: () => void;
}

type LayerSource = 'satellite' | 'drone';

const LayerSelector: React.FC<LayerSelectorProps> = ({
  selectedLayer,
  onSelect,
  onClose,
  layerOptions,
  onRemoveLayer,
  onRemoveDroneLayer,
  selectedDateLabel,
  canViewPastLayers = false,
  onViewPastLayers,
  layersLoading,
  droneLayerOptions,
  selectedDroneLayer,
  onDroneLayerSelect,
}) => {
  const [layerSource, setLayerSource] = useState<LayerSource>('satellite');

  return (
    <div className="layer-selector-container">
      <div className="layer-selector-header">
        <div className="layer-title">Layers</div>
        <button className="close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="layer-source-toggle">
        <button
          className={`layer-source-button ${
            layerSource === 'satellite' ? 'active' : ''
          }`}
          onClick={() => setLayerSource('satellite')}
        >
          Satellite
        </button>
        <button
          className={`layer-source-button ${
            layerSource === 'drone' ? 'active' : ''
          }`}
          onClick={() => setLayerSource('drone')}
        >
          Drone
        </button>
      </div>

      {layerSource === 'satellite' &&
        (selectedDateLabel || onViewPastLayers) && (
          <div className="layer-history-row">
            {selectedDateLabel && (
              <div className="layer-current-date">{selectedDateLabel}</div>
            )}
            {onViewPastLayers && (
              <button
                className="view-past-layers-button"
                onClick={onViewPastLayers}
                disabled={!canViewPastLayers}
              >
                View past layers
              </button>
            )}
          </div>
        )}

      {layerSource === 'satellite' ? (
        <div className="layer-grid">
          {layersLoading
            ? // If we're still fetching layer data, show skeleton placeholders
              layerOptions.map((_, idx) => (
                <div key={idx} className="layer-skeleton">
                  <Skeleton variant="rect" width={70} height={70} />
                  <Skeleton variant="text" width={50} style={{marginTop: 8}} />
                </div>
              ))
            : // Normal rendering if not loading
              layerOptions.map((layer) => {
                const isSelected = layer.key === selectedLayer.key;
                return (
                  <button
                    key={layer.key}
                    className={`layer-button ${isSelected ? 'selected' : ''}`}
                    onClick={() => onSelect(layer.key)}
                  >
                    <div className="icon-bg">{layer.icon}</div>
                    <div className="layer-label">{layer.label}</div>
                  </button>
                );
              })}
        </div>
      ) : (
        <div className="layer-grid">
          {layersLoading
            ? // If we're still fetching layer data, show skeleton placeholders
              droneLayerOptions.map((_, idx) => (
                <div key={idx} className="layer-skeleton">
                  <Skeleton variant="rect" width={70} height={70} />
                  <Skeleton variant="text" width={50} style={{marginTop: 8}} />
                </div>
              ))
            : // Normal rendering if not loading
              droneLayerOptions.map((layer) => {
                const isSelected = layer.key === selectedDroneLayer?.key;
                return (
                  <button
                    key={layer.key}
                    className={`layer-button ${isSelected ? 'selected' : ''}`}
                    onClick={() => onDroneLayerSelect(layer.key)}
                  >
                    <div className="icon-bg">{layer.icon}</div>
                    <div className="layer-label">{layer.label}</div>
                  </button>
                );
              })}
        </div>
      )}

      {layerSource === 'satellite' && onRemoveLayer && (
        <button
          className="remove-layer-button"
          onClick={() => {
            onRemoveLayer();
            onClose();
          }}
        >
          Remove Layer
        </button>
      )}
      {layerSource === 'drone' && onRemoveDroneLayer && (
        <button
          className="remove-layer-button"
          onClick={() => {
            onRemoveDroneLayer();
            onClose();
          }}
        >
          Remove Layer
        </button>
      )}
    </div>
  );
};

export default LayerSelector;
